<template>
  <div class="zendesk-widget-container">
    <button
      :style="{
        backgroundColor: bgColor,
        color: textColor
      }"
      type="button"
      class="zendesk-btn"
      @click="loadZendeskScript"
      v-if="isButtonShown"
    >
      <span class="zendesk-btn-icon">
        <svg
          viewBox="0 0 15 16"
          aria-hidden="true"
          :style="{ fill: textColor }"
        >
          <path
            d="M1.3,16c-0.7,0-1.1-0.3-1.2-0.8c-0.3-0.8,0.5-1.3,0.8-1.5c0.6-0.4,0.9-0.7,1-1c0-0.2-0.1-0.4-0.3-0.7c0,0,0-0.1-0.1-0.1 C0.5,10.6,0,9,0,7.4C0,3.3,3.4,0,7.5,0C11.6,0,15,3.3,15,7.4s-3.4,7.4-7.5,7.4c-0.5,0-1-0.1-1.5-0.2C3.4,15.9,1.5,16,1.5,16 C1.4,16,1.4,16,1.3,16z M3.3,10.9c0.5,0.7,0.7,1.5,0.6,2.2c0,0.1-0.1,0.3-0.1,0.4c0.5-0.2,1-0.4,1.6-0.7c0.2-0.1,0.4-0.2,0.6-0.1 c0,0,0.1,0,0.1,0c0.4,0.1,0.9,0.2,1.4,0.2c3,0,5.5-2.4,5.5-5.4S10.5,2,7.5,2C4.5,2,2,4.4,2,7.4c0,1.2,0.4,2.4,1.2,3.3 C3.2,10.8,3.3,10.8,3.3,10.9z"
          ></path>
        </svg>
      </span>
      <span class="zendesk-btn-lbl">{{ label }}</span>
    </button>
  </div>
</template>

<script>
export default {
  name: 'ZendeskWebWidget',
  props: {
    widgetKey: {
      type: String,
      required: true
    },
    bgColor: {
      type: String,
      required: true
    },
    textColor: {
      type: String,
      required: true
    },
    label: {
      type: String,
      default: 'help'
    },
    languageCode: {
      type: String
    }
  },
  data() {
    return {
      isButtonShown: true
    }
  },
  methods: {
    loadZendeskScript() {
      const widgetKey = this.widgetKey

      // track click event
      this.trackZendeskWidgetClicked()

      if (widgetKey.length === 0 || window.zE) {
        return
      }

      // Static configuration needs to be declared before being used by the Zendesk widget
      window.zESettings = {
        webWidget: {
          chat: {
            suppress: false,
            connectOnPageLoad: false
          },
          contactForm: {
            suppress: false
          },
          helpCenter: {
            suppress: false
          },
          talk: {
            suppress: false
          },
          answerBot: {
            suppress: false
          }
        }
      }

      const src = `https://static.zdassets.com/ekr/snippet.js?key=${widgetKey}`
      const zendeskScript = document.createElement('script')
      zendeskScript.setAttribute('id', 'ze-snippet')
      zendeskScript.setAttribute('src', src)
      document.head.appendChild(zendeskScript)

      const onloadListener = () => {
        if (this.languageCode) {
          window.zE(
            'messenger:set',
            'locale',
            this.localeMapper(this.languageCode)
          )
        }

        zE('messenger:on', 'open', () => {
          this.isButtonShown = false
        })

        zE('messenger', 'open')
        zendeskScript.removeEventListener('load', onloadListener)
      }
      zendeskScript.addEventListener('load', onloadListener)
    },
    trackZendeskWidgetClicked() {
      if (this.$analytics) {
        const params = {
          page: window.location.href
        }

        this.$analytics.trackEvent('click_help_icon', { params })
      }
    },
    localeMapper(languageCode) {
      /** Available language codes: https://support.zendesk.com/hc/en-us/articles/203761906 */
      if (languageCode === 'en') {
        return 'en-us'
      } else if (languageCode === 'zh') {
        return 'zh-tw'
      }

      return languageCode
    }
  }
}
</script>

<style lang="scss" scoped>
$base-font-size: 14px;
@function calcFontSize($size-in-rem) {
  @return calc(#{$size-in-rem} * #{$base-font-size});
}

.zendesk-widget-container {
  height: 50px;
  margin: 10px 20px;
  position: fixed;
  bottom: 0px;
  overflow: visible;
  opacity: 1;
  border: 0px;
  z-index: 999997;
  right: 0px;
  .zendesk-btn {
    font-family: system-ui, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto,
      Oxygen-Sans, Ubuntu, Cantarell, Helvetica Neue, Arial, sans-serif;
    padding: calcFontSize(0.92857);
    border-radius: calcFontSize(999);
    border: none;
    font-weight: 700;
    cursor: pointer !important;
    display: flex;
    .zendesk-btn-icon {
      display: flex;
      max-width: 100%;
      svg {
        min-width: calcFontSize(1.42857);
        min-height: calcFontSize(1.42857);
        height: calcFontSize(1.42857);
        width: calcFontSize(1.42857);
      }
    }
    .zendesk-btn-lbl {
      margin-left: calcFontSize(0.57143);
      text-transform: capitalize;
      letter-spacing: 0.6px;
      font-size: calcFontSize(1.07143);
      display: none;
    }
  }
}

@include desktop {
  .zendesk-widget-container {
    .zendesk-btn {
      padding: calcFontSize(0.92857) calcFontSize(1.57143);
      .zendesk-btn-lbl {
        display: initial;
      }
    }
  }
}
</style>
